import { ChangeEvent, useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import Select from "react-select";
import { addedHours, addsHours, defaultAdded, originalNote } from "./scopeLines";
import { DASH, formatHours, formatMoney } from "./format";
import type {
  PreviewLine,
  RevisedMap,
  RevisedValues,
  ScopeLine,
  ScopeService,
} from "./types";
import "./builder.css";

export interface ResourceOption {
  value: string;
  label: string;
}

export interface ChangeScopeGridProps {
  title: string;
  services: ScopeService[];
  revised: RevisedMap;
  onRevise: (key: string, values: RevisedValues) => void;
  resourceOptions: ResourceOption[];
  previewLines: Record<string, PreviewLine> | null;
  currency: string;
}

// Blank, unparsable, negative, equal-to-default, or (for a whole-number field)
// fractional input means "unset" (the server's default applies).
const parsed = (value: string, fallback: number, integer = false) => {
  if (value.trim() === "") return undefined;
  const n = Number(value);
  if (isNaN(n) || n < 0 || n === fallback) return undefined;
  return integer && !Number.isInteger(n) ? undefined : n;
};

const negative = (value: string) => value.trim() !== "" && Number(value) < 0;

const fractional = (value: string) =>
  value.trim() !== "" && !isNaN(Number(value)) && !Number.isInteger(Number(value));

interface NumberCellProps {
  fallback: number; // the default added value
  value?: number;
  step: number | "any";
  integer?: boolean;
  label: string;
  onChange: (value: number | undefined) => void;
}

// Keeps the typed text locally so a field can be cleared and retyped.
const NumberCell = ({
  fallback,
  value,
  step,
  integer = false,
  label,
  onChange,
}: NumberCellProps) => {
  const [text, setText] = useState(String(value ?? fallback));

  // Resync after an external change (e.g. a reset) that the text doesn't explain.
  useEffect(() => {
    setText((current) =>
      parsed(current, fallback, integer) === value
        ? current
        : String(value ?? fallback)
    );
  }, [value, fallback, integer]);

  const below = negative(text);
  const notWhole = integer && !below && fractional(text);
  const invalid = below || notWhole;
  return (
    <>
      <Form.Control
        type="number"
        size="sm"
        min={0}
        step={step}
        aria-label={label}
        isInvalid={invalid}
        className={value !== undefined && !invalid ? "customItem" : undefined}
        value={text}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          setText(e.target.value);
          onChange(parsed(e.target.value, fallback, integer));
        }}
        onBlur={() => {
          if (parsed(text, fallback, integer) === undefined) setText(String(fallback));
        }}
      />
      {below && (
        <Form.Control.Feedback type="invalid">Enter zero or more</Form.Control.Feedback>
      )}
      {notWhole && (
        <Form.Control.Feedback type="invalid">Whole numbers only</Form.Control.Feedback>
      )}
    </>
  );
};

const ChangeScopeGrid = ({
  title,
  services,
  revised,
  onRevise,
  resourceOptions,
  previewLines,
  currency,
}: ChangeScopeGridProps) => {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [onlyChanged, setOnlyChanged] = useState(false);

  if (services.length === 0) return null;

  // Added hours; a custom-hours line's come from the preview (null while pending).
  const added = (line: ScopeLine): number | null => {
    const r = revised[line.key];
    if (!addsHours(line, r)) return 0;
    const hours = addedHours(line, r);
    if (hours !== null) return hours;
    return previewLines?.[line.key] ? previewLines[line.key].hours : null;
  };

  const price = (line: ScopeLine) =>
    addsHours(line, revised[line.key])
      ? previewLines?.[line.key]
        ? Number(previewLines[line.key].revenue)
        : null
      : 0;

  const hoursText = (lines: ScopeLine[]) => {
    if (!lines.some((l) => addsHours(l, revised[l.key]))) return "";
    const values = lines.map(added);
    if (values.some((v) => v === null)) return "…";
    return `+${formatHours((values as number[]).reduce((a, b) => a + b, 0))}`;
  };

  const priceText = (lines: ScopeLine[]) => {
    const changed = lines.filter((l) => addsHours(l, revised[l.key]));
    if (changed.length === 0) return "";
    const values = changed.map(price);
    if (values.some((v) => v === null)) return DASH;
    return formatMoney(
      (values as number[]).reduce((a, b) => a + b, 0),
      currency,
      true
    );
  };

  const optionsFor = (line: ScopeLine): ResourceOption[] =>
    line.resourceId && !resourceOptions.some((o) => o.value === line.resourceId)
      ? [...resourceOptions, { value: line.resourceId, label: line.resourceLabel }]
      : resourceOptions;

  const row = (line: ScopeLine, group: ScopeLine[], isSub: boolean) => {
    const r = revised[line.key] ?? {};
    const defaults = defaultAdded(line);
    const options = optionsFor(line);
    const resourceId = r.resourceId ?? line.resourceId;
    const isCollapsed = collapsed[line.key];
    return (
      <tr key={line.key} className={isSub ? "changeScopeGridSub" : undefined}>
        <td>
          {!isSub && group.length > 1 && (
            <button
              type="button"
              className="btn btn-link btn-sm p-0 mr-2"
              aria-label={`${isCollapsed ? "Expand" : "Collapse"} ${line.name}`}
              onClick={() =>
                setCollapsed((prev) => ({ ...prev, [line.key]: !isCollapsed }))
              }
            >
              {isCollapsed ? "▸" : "▾"}
            </button>
          )}
          {line.name}
          <div className="changeScopeGridHelp">{originalNote(line)}</div>
        </td>
        <td>
          <NumberCell
            fallback={defaults.quantity}
            value={r.quantity}
            step={1}
            integer
            label={`Quantity for ${line.name}`}
            onChange={(quantity) => onRevise(line.key, { quantity })}
          />
        </td>
        <td>
          {line.customHours ? (
            <span className="changeScopeGridReadOnly" title="Calculated by the catalog">
              {DASH}
            </span>
          ) : (
            <NumberCell
              fallback={defaults.effort}
              value={r.effort}
              step="any"
              label={`Effort for ${line.name}`}
              onChange={(effort) => onRevise(line.key, { effort })}
            />
          )}
        </td>
        <td>
          <Select
            className={
              r.resourceId !== undefined ? "resourceDropdown customItem" : "resourceDropdown"
            }
            classNamePrefix="resourceDropdown"
            aria-label={`Resource for ${line.name}`}
            placeholder={DASH}
            options={options}
            value={options.find((o) => o.value === resourceId) ?? null}
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            onChange={(option) =>
              onRevise(line.key, {
                resourceId:
                  !option || option.value === line.resourceId ? undefined : option.value,
              })
            }
          />
        </td>
        <td>{hoursText(group)}</td>
        <td>{priceText(group)}</td>
      </tr>
    );
  };

  const visible = (s: ScopeService) =>
    !onlyChanged ||
    [s.line, ...s.subservices].some((l) => addsHours(l, revised[l.key]));

  return (
    <div className="changeScopeGrid mb-4">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="mb-0">{title}</h6>
        <Form.Check
          type="switch"
          id={`only-changed-${title.toLowerCase().replace(/\W+/g, "-")}`}
          label="Only changed lines"
          checked={onlyChanged}
          onChange={() => setOnlyChanged((v) => !v)}
        />
      </div>
      <table className="table table-sm">
        <thead>
          <tr>
            <th>Service</th>
            <th>Quantity</th>
            <th>Effort (hrs each)</th>
            <th>Resource for addl effort</th>
            <th>Hours</th>
            <th>Change $</th>
          </tr>
        </thead>
        <tbody>
          {services.filter(visible).flatMap((s) => {
            const group = [s.line, ...s.subservices];
            const subs = collapsed[s.line.key]
              ? []
              : s.subservices.filter(
                  (l) => !onlyChanged || addsHours(l, revised[l.key])
                );
            return [row(s.line, group, false), ...subs.map((l) => row(l, [l], true))];
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ChangeScopeGrid;
